import React, { useState, useEffect, useMemo } from "react";
import { useForm, type UseFormReturn } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslations } from "use-intl";
import { Invoice } from "@/types/payments";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, AlertTriangle } from "lucide-react";
import { InvoiceUploadService } from "@/services/invoiceUploadService";
import DocumentViewerPanel from "@/components/shared/DocumentViewerPanel";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable";
import { ServiceRegistry } from "@/services/ServiceRegistry";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { getExpenseCategoryOptions } from "@/constants/expenseCategories";
import { ParticipantsField } from "@/components/expenses/ParticipantsField";
import { toast } from "sonner";
import { useFriendlyError } from "@/i18n/useFriendlyError";
import { StructuredRowsEditor } from "@/components/upload/StructuredRowsEditor";
import { buildFinalizeUpdates } from "@/components/upload/buildFinalizeUpdates";
import {
  createDraftFormSchema,
  DOCUMENT_TYPES,
  PAYMENT_METHODS,
  type DraftFormInput,
  type DraftFormValues,
} from "@/components/upload/draftFormSchema";
import { userFacingErrorMessage } from '@/utils/userFacingError';

type DraftForm = UseFormReturn<DraftFormInput, unknown, DraftFormValues>;
type TextFieldName = Exclude<keyof DraftFormInput, "participants" | "documentType" | "paymentMethod">;

function isDocumentType(type: string | undefined): type is (typeof DOCUMENT_TYPES)[number] {
  return (DOCUMENT_TYPES as readonly string[]).includes(type ?? "");
}

function isPaymentMethod(method: string | undefined): method is (typeof PAYMENT_METHODS)[number] {
  return (PAYMENT_METHODS as readonly string[]).includes(method ?? "");
}

interface DraftTextFieldProps {
  form: DraftForm;
  name: TextFieldName;
  label: string;
  placeholder?: string;
  type?: "text" | "number" | "date";
  step?: string;
  withMessage?: boolean;
}

/** One labelled input bound to the draft form. */
const DraftTextField: React.FC<DraftTextFieldProps> = ({ form, name, label, placeholder, type, step, withMessage = true }) => (
  <FormField
    control={form.control}
    name={name}
    render={({ field }) => (
      <FormItem>
        <FormLabel>{label}</FormLabel>
        <FormControl>
          <Input type={type} step={step} placeholder={placeholder} {...field} value={(field.value as string | number | undefined) ?? ""} />
        </FormControl>
        {withMessage && <FormMessage />}
      </FormItem>
    )}
  />
);

interface DraftSelectFieldProps {
  form: DraftForm;
  name: "documentType" | "paymentMethod" | "expenseCategory";
  label: React.ReactNode;
  placeholder: string;
  options: { value: string; label: string }[];
}

/** One labelled select bound to the draft form. */
const DraftSelectField: React.FC<DraftSelectFieldProps> = ({ form, name, label, placeholder, options }) => (
  <FormField
    control={form.control}
    name={name}
    render={({ field }) => (
      <FormItem>
        <FormLabel>{label}</FormLabel>
        <Select onValueChange={field.onChange} value={field.value || ""}>
          <FormControl>
            <SelectTrigger>
              <SelectValue placeholder={placeholder} />
            </SelectTrigger>
          </FormControl>
          <SelectContent>
            {options.map((option) => (
              <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <FormMessage />
      </FormItem>
    )}
  />
);

interface DraftDetailDialogProps {
  draft: Invoice | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onFinalized?: () => void;
  onDeleted?: () => void;
}

function warningsHeading(
  finalizationError: string | undefined,
  extractionStatus: string | undefined,
): "autoFinalizationFailed" | "finalizationIssue" | "extractionIssues" {
  if (finalizationError) return "autoFinalizationFailed";
  return extractionStatus === "completed" ? "finalizationIssue" : "extractionIssues";
}

function reviewReasonKey(extractionStatus: string | undefined): "completed" | "partial" | "failed" {
  if (extractionStatus === "completed") return "completed";
  return extractionStatus === "partial" ? "partial" : "failed";
}

const DraftDetailDialog: React.FC<DraftDetailDialogProps> = ({
  draft,
  open,
  onOpenChange,
  onFinalized,
  onDeleted,
}) => {
  const t = useTranslations("consultantDraft");
  const validation = useTranslations("consultantDraft.validation");
  const { friendlyError, friendlyWarnings } = useFriendlyError();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const draftFormSchema = useMemo(() => createDraftFormSchema(validation), [validation]);
  const form = useForm<DraftFormInput, unknown, DraftFormValues>({
    resolver: zodResolver(draftFormSchema),
    defaultValues: {
      documentType: undefined,
      vendor: "",
      amount: undefined,
      currency: "SEK",
      dueDate: "",
      transactionDate: "",
      remittanceInformation: "",
      vendorIban: "",
      vendorBankgiro: "",
      vendorPlusgiro: "",
      vendorAccountNumber: "",
      vendorClearingNumber: "",
      bankBic: "",
      bankName: "",
      paymentMethod: undefined,
      expenseCategory: "",
      participants: [],
    },
  });

  // Pre-fill form and fetch file URL when draft changes
  useEffect(() => {
    if (draft && open) {
      form.reset({
        documentType: isDocumentType(draft.documentType) ? draft.documentType : undefined,
        vendor: draft.vendor || "",
        // An unextracted draft has no amount: leave the field empty rather than prefill a 0.
        amount: draft.amount ?? undefined,
        currency: draft.currency || "SEK",
        dueDate: draft.dueDate || "",
        transactionDate: draft.transactionDate || "",
        remittanceInformation: draft.remittanceInformation || "",
        vendorIban: draft.vendorIban || "",
        vendorBankgiro: draft.vendorBankgiro || "",
        vendorPlusgiro: draft.vendorPlusgiro || "",
        vendorAccountNumber: draft.vendorAccountNumber || "",
        vendorClearingNumber: draft.vendorClearingNumber || "",
        bankBic: draft.bankBic || "",
        bankName: draft.bankName || "",
        paymentMethod: isPaymentMethod(draft.paymentMethod) ? draft.paymentMethod : undefined,
        expenseCategory: draft.expenseCategory || "",
        participants: [],
        // Pre-fill sales report fields from extraction metadata
        reportDate: draft.extractionMetadata?.report_date || "",
        totalGrossSales: draft.extractionMetadata?.total_gross_sales || 0,
        totalReturns: draft.extractionMetadata?.total_returns || 0,
        totalNetSales: draft.extractionMetadata?.total_net_sales || 0,
        vatSummary: draft.extractionMetadata?.vat_summary
          ? JSON.stringify(draft.extractionMetadata.vat_summary, null, 2) : "",
        paymentMethods: draft.extractionMetadata?.payment_methods
          ? JSON.stringify(draft.extractionMetadata.payment_methods, null, 2) : "",
        salesCategories: draft.extractionMetadata?.sales_categories
          ? JSON.stringify(draft.extractionMetadata.sales_categories, null, 2) : "",
      });
      setError(null);
    }
  }, [draft, open, form]);

  if (!draft) return null;

  const extractionStatus = draft.extractionStatus;
  const extractionMetadata = draft.extractionMetadata;
  const warnings = extractionMetadata?.warnings as string[] | undefined;
  const fieldsMissing = extractionMetadata?.fieldsMissing as string[] | undefined;
  const finalizationError = extractionMetadata?.finalizationError as string | undefined;
  const watchedDocumentType = form.watch("documentType");
  const isPayable = watchedDocumentType === "payable";
  const isReceiptOrExpense = watchedDocumentType === "receipt" || watchedDocumentType === "expense";
  const isSalesReport = watchedDocumentType === "sales_report";
  const isProcessing = extractionStatus === "pending" || extractionStatus === "processing";

  const documentTypeOptions = DOCUMENT_TYPES.map((value) => ({ value, label: t(`documentTypes.${value}`) }));
  const paymentMethodOptions = PAYMENT_METHODS.map((value) => ({ value, label: t(`receipt.paymentMethods.${value}`) }));

  const onSubmit = async (data: DraftFormValues) => {
    setIsSubmitting(true);
    setError(null);

    try {
      const finalizeData = buildFinalizeUpdates(data);
      if (!finalizeData) {
        setError(t("errors.invalidJson"));
        setIsSubmitting(false);
        return;
      }

      const result = await new InvoiceUploadService().finalizeDraftInvoice(draft.id, finalizeData);

      if (result.success) {
        toast.success(t("toasts.finalized"));
        onOpenChange(false);
        onFinalized?.();
      } else if (result.alreadyFinalized) {
        // Race with the background extraction job's auto-finalize — the
        // document is already finalized, but not with these edits. Be
        // honest about that instead of showing a scary "not found" error.
        toast.info(t("toasts.alreadyFinalized"));
        onOpenChange(false);
        onFinalized?.();
      } else if (result.error) {
        setError(friendlyError(result.error));
      }
    } catch (err) {
      const message = friendlyError(userFacingErrorMessage(err, t("errors.finalizeFailed")));
      setError(message);
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const warningsBlock = (finalizationError || (warnings && warnings.length > 0) || (fieldsMissing && fieldsMissing.length > 0)) ? (
    <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg space-y-1">
      <div className="flex items-center gap-1 text-yellow-700 text-sm font-medium">
        <AlertTriangle className="h-4 w-4" />
        {t(`warnings.${warningsHeading(finalizationError, extractionStatus)}`)}
      </div>
      {finalizationError && (
        <p className="text-xs text-yellow-700">{friendlyError(finalizationError)}</p>
      )}
      <ul className="text-xs text-yellow-700 list-disc list-inside">
        {friendlyWarnings(warnings ?? []).map((w, i) => (
          <li key={`w-${i}`}>{w}</li>
        ))}
        {fieldsMissing?.length ? (
          <li>{t("warnings.missingFields", { fields: fieldsMissing.join(", ") })}</li>
        ) : null}
      </ul>
    </div>
  ) : null;

  const handleDelete = async () => {
    if (!draft?.id) return;
    setDeleteLoading(true);
    try {
      const service = ServiceRegistry.getInvoiceUploadService();
      await service.deleteDocument(draft.id);
      setShowDeleteConfirm(false);
      onOpenChange(false);
      toast.success(t("toasts.deleted"));
      onDeleted?.();
    } catch (error) {
      console.error('Failed to delete document:', error);
      toast.error(t("toasts.deleteFailed"));
    } finally {
      setDeleteLoading(false);
    }
  };

  const formContent = (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <DraftSelectField
          form={form}
          name="documentType"
          label={
            <>
              {t("fields.documentType")}
              {!watchedDocumentType && <span className="text-destructive ml-1">*</span>}
            </>
          }
          placeholder={t("fields.selectDocumentType")}
          options={documentTypeOptions}
        />

        <DraftTextField form={form} name="vendor" label={t("fields.vendor")} placeholder={t("fields.vendorPlaceholder")} />

        <div className="grid grid-cols-2 gap-3">
          <DraftTextField form={form} name="amount" label={t("fields.amount")} type="number" step="0.01" placeholder="0.00" />
          <DraftTextField form={form} name="currency" label={t("fields.currency")} placeholder={t("fields.currencyPlaceholder")} />
        </div>

        {!isReceiptOrExpense && !isSalesReport && (
          <DraftTextField form={form} name="dueDate" label={t("fields.dueDate")} type="date" />
        )}

        <DraftTextField
          form={form}
          name="remittanceInformation"
          label={t("fields.remittance")}
          placeholder={t("fields.remittancePlaceholder")}
        />

        {/* Payable-specific fields */}
        {isPayable && (
          <div className="space-y-3 p-3 border rounded-lg bg-muted/50">
            <p className="text-sm font-medium">{t("payable.heading")}</p>
            <div className="space-y-2">
              <DraftTextField form={form} name="vendorIban" label={t("payable.iban")} placeholder={t("payable.ibanPlaceholder")} />
              <div className="grid grid-cols-2 gap-2">
                <DraftTextField form={form} name="vendorBankgiro" label={t("payable.bankgiro")} placeholder={t("payable.bankgiroPlaceholder")} />
                <DraftTextField form={form} name="vendorPlusgiro" label={t("payable.plusgiro")} placeholder={t("payable.plusgiroPlaceholder")} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <DraftTextField
                  form={form}
                  name="vendorAccountNumber"
                  label={t("payable.accountNumber")}
                  placeholder={t("payable.accountNumberPlaceholder")}
                />
                <DraftTextField
                  form={form}
                  name="vendorClearingNumber"
                  label={t("payable.clearingNumber")}
                  placeholder={t("payable.clearingNumberPlaceholder")}
                />
              </div>
            </div>
            <DraftTextField form={form} name="bankBic" label={t("payable.bic")} placeholder={t("payable.bicPlaceholder")} />
            <DraftTextField form={form} name="bankName" label={t("payable.bankName")} placeholder={t("payable.bankNamePlaceholder")} />
          </div>
        )}

        {/* Purchase date — receipt/expense only, outside the amber box so it's always visible */}
        {isReceiptOrExpense && (
          <DraftTextField form={form} name="transactionDate" label={t("fields.purchaseDate")} type="date" />
        )}

        {/* Receipt/expense-specific fields */}
        {isReceiptOrExpense && (
          <div className="space-y-3 p-3 border rounded-lg bg-amber-50">
            <p className="text-sm font-medium">{t("receipt.heading")}</p>
            <DraftSelectField
              form={form}
              name="paymentMethod"
              label={t("fields.paymentMethod")}
              placeholder={t("fields.selectPaymentMethod")}
              options={paymentMethodOptions}
            />
            <DraftSelectField
              form={form}
              name="expenseCategory"
              label={t("fields.expenseCategory")}
              placeholder={t("fields.selectCategory")}
              options={getExpenseCategoryOptions()}
            />

            <ParticipantsField
              control={form.control}
              setValue={form.setValue}
              watch={form.watch}
              categoryFieldName="expenseCategory"
              participantsFieldName="participants"
            />
          </div>
        )}

        {/* Sales report-specific fields */}
        {isSalesReport && (
          <div className="space-y-3 p-3 border rounded-lg bg-blue-50">
            <p className="text-sm font-medium">{t("zReport.heading")}</p>
            <DraftTextField form={form} name="reportDate" label={t("fields.reportDate")} type="date" />
            <div className="grid grid-cols-3 gap-3">
              <DraftTextField form={form} name="totalGrossSales" label={t("fields.grossSales")} type="number" step="0.01" withMessage={false} />
              <DraftTextField form={form} name="totalReturns" label={t("fields.returns")} type="number" step="0.01" withMessage={false} />
              <DraftTextField form={form} name="totalNetSales" label={t("fields.netSales")} type="number" step="0.01" withMessage={false} />
            </div>

            <FormField
              control={form.control}
              name="vatSummary"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("rows.vat.label")}</FormLabel>
                  <StructuredRowsEditor
                    value={field.value}
                    onChange={field.onChange}
                    addLabel={t("rows.vat.add")}
                    newRow={{ rate: 0, vat_amount: 0, net_amount: 0, gross_amount: 0 }}
                    columns={[
                      { key: "rate", kind: "number", label: t("rows.vat.rate") },
                      { key: "vat_amount", kind: "number", label: t("rows.vat.vat") },
                      { key: "net_amount", kind: "number", label: t("rows.vat.net") },
                      { key: "gross_amount", kind: "number", label: t("rows.vat.gross") },
                    ]}
                  />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="paymentMethods"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("rows.methods.label")}</FormLabel>
                  <StructuredRowsEditor
                    value={field.value}
                    onChange={field.onChange}
                    addLabel={t("rows.methods.add")}
                    newRow={{ method: "card", provider: "", amount: 0 }}
                    columns={[
                      {
                        key: "method",
                        kind: "select",
                        label: t("rows.methods.method"),
                        defaultValue: "card",
                        options: (["card", "swish", "cash", "other"] as const).map((value) => ({
                          value,
                          label: t(`rows.methods.options.${value}`),
                        })),
                      },
                      { key: "provider", kind: "text", label: t("rows.methods.provider") },
                      { key: "amount", kind: "number", label: t("rows.methods.amount") },
                    ]}
                  />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="salesCategories"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("rows.categories.label")}</FormLabel>
                  <StructuredRowsEditor
                    value={field.value}
                    onChange={field.onChange}
                    addLabel={t("rows.categories.add")}
                    newRow={{ category: "", net: 0, vat: 0, gross: 0, vat_rate: 0 }}
                    columns={[
                      { key: "category", kind: "text", label: t("rows.categories.category") },
                      { key: "net", kind: "number", label: t("rows.categories.net") },
                      { key: "vat", kind: "number", label: t("rows.categories.vat") },
                      { key: "gross", kind: "number", label: t("rows.categories.gross") },
                      { key: "vat_rate", kind: "number", label: t("rows.categories.vatRate") },
                    ]}
                  />
                </FormItem>
              )}
            />
          </div>
        )}

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <div className="flex gap-2 pt-2">
          <Button type="submit" className="flex-1" disabled={isSubmitting || !watchedDocumentType}>
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t("actions.finalizing")}
              </>
            ) : (
              t("actions.finalize")
            )}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            {t("actions.cancel")}
          </Button>
          {draft?.isDraft && (
            <>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={deleteLoading}
              >
                {deleteLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("actions.delete")}
              </Button>

              <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>{t("actions.deleteTitle")}</AlertDialogTitle>
                    <AlertDialogDescription>{t("actions.deleteBody")}</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>{t("actions.cancel")}</AlertDialogCancel>
                    <AlertDialogAction onClick={handleDelete} disabled={deleteLoading}>
                      {t("actions.delete")}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </>
          )}
        </div>
      </form>
    </Form>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={!isProcessing && draft.filePath
        ? "max-w-5xl h-[90vh] flex flex-col"
        : "max-w-2xl max-h-[90vh] overflow-y-auto"}>
        <DialogHeader>
          <DialogTitle>{draft.fileName || t("title")}</DialogTitle>
          <DialogDescription>
            {isProcessing ? t("status.processing") : t(`status.${reviewReasonKey(extractionStatus)}`)}
          </DialogDescription>
        </DialogHeader>

        {/* Processing state */}
        {isProcessing && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-blue-600 mr-2" />
            <span className="text-sm text-blue-700">{t("status.processingInline")}</span>
          </div>
        )}

        {/* Split layout when document available */}
        {!isProcessing && draft.filePath && (
          <ResizablePanelGroup direction="horizontal" className="flex-1 mt-2">
            <ResizablePanel defaultSize={50} minSize={30}>
              <DocumentViewerPanel filePath={draft.filePath!} fileName={draft.fileName} />
            </ResizablePanel>
            <ResizableHandle withHandle />
            <ResizablePanel defaultSize={50} minSize={30} className="pl-4">
              <div className="h-full overflow-y-auto space-y-4 pr-2 pb-4">
                {warningsBlock}
                {formContent}
              </div>
            </ResizablePanel>
          </ResizablePanelGroup>
        )}

        {/* No-document fallback: warnings + form stacked */}
        {!isProcessing && !draft.filePath && (
          <div className="space-y-4 mt-2">
            {warningsBlock}
            {formContent}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DraftDetailDialog;
